<template>
  <div>
    <v-row dense>
      <v-col cols="12" sm="6" xs="12">
        <v-card flat class="mt-3 py-3 mr-2" height="570">
          <v-card-title>
            <h3
                class="font-weight-black d-none d-md-block text-capitalize"
                :class="$vuetify.theme.isDark ? 'white--text' : 'black--text'"
            >
              Top Sales
            </h3>
            <h6
                class="font-weight-black d-md-none d-sm-block text-capitalize"
                :class="$vuetify.theme.isDark ? 'white--text' : 'black--text'"
            >
              Top Sales
            </h6>
          </v-card-title>
          <v-card-text class="pa-0 text-center">
            <v-skeleton-loader
                type="card"
                v-if="!topSales || progress"
            ></v-skeleton-loader>

            <div v-else-if="topSales.length < 1">
              <norecords-component
                  title="No sales yet!"
                  description="Your top 5 sales would show here."
              ></norecords-component>
              <v-btn
                  @click.native="$store.state.newcashexpense = true"
                  to="/invoices/new"
                  color="blue darken-4"
                  rounded
                  x-small
                  text
              >Record a sale
              </v-btn>
            </div>
            <v-list-item v-else class="rounded-lg my-1">
              <v-list-item-content style="width: 100%">
                <v-list-item-title
                    v-for="(item, index) in topSales"
                    :key="index"
                    style="margin-bottom: 50px"
                >
                  <div class="d-flex align-center">
                    <v-list-item-title
                        class="font-weight-light"
                        style="font-size: 0.9rem; text-align: left"
                    >
                      {{ item.name }}
                    </v-list-item-title>
                    <div style="font-size: 0.9rem" class="fw-bold">
                      {{
                        Number(item.sales_amount)
                            | short_number
                            | currency_symbol
                      }}
                      ({{ calculatePercentage(item) }}%)
                    </div>
                  </div>
                  <v-progress-linear
                      rounded
                      color="blue darken-4"
                      :value="calculatePercentage(item)"
                      style="padding: 0 !important; margin: 0 !important"
                  ></v-progress-linear>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" xs="12">
        <v-card flat class="mt-3 py-3 ml-1" height="570">
          <v-card-title class="font-weight-light">
            <h3 class="font-weight-black d-none d-md-block text-capitalize">
              {{ $t("dashboard.expense_title") }}
            </h3>
            <h6 class="font-weight-black d-md-none d-sm-block text-capitalize">
              {{ $t("dashboard.expense_title") }}
            </h6>
          </v-card-title>
          <v-skeleton-loader
              type="card"
              v-if="!series.length && progress"
          ></v-skeleton-loader>
          <v-card-text class="pa-0 text-center">
            <apexchart
                v-if="!progress && series.length"
                type="donut"
                height="350"
                :options="chartOptions"
                :series="series"
            ></apexchart>

            <div v-else-if="series.length < 1 && !progress">
              <norecords-component
                  :title="$t('dashboard.no_expense')"
                  description="Your top expenses would show here"
              ></norecords-component>
              <v-btn
                  @click.native="$store.state.newcashexpense = true"
                  to="/paidexpenses"
                  color="blue darken-4"
                  rounded
                  x-small
                  text
              >{{ $t("dashboard.record_first_expense") }}
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import NorecordsComponent from "../sync/norecordsComponent";
import eventBus from "../../utils";
import moment from "moment/moment";

export default {
  name: "expenses",
  components: {NorecordsComponent},
  data() {
    return {
      dummyData: [
        {
          name: "item1",
          percentage: "40",
          amount: 1000,
        },
        {
          name: "Accounting item2",
          percentage: "30",
          amount: 10000,
        },
        {
          name: "Shell item4",
          percentage: "70",
          amount: 100000,
        },
        {
          name: "Functional item5",
          percentage: "70",
          amount: 100000,
        },
        {
          name: "item3",
          percentage: "70",
          amount: 100000,
        },
      ],
      progress: false,
      expenseAccounts: [],
      series: [],
      chartLabels: [],
      chartOptions: {
        labels: [],
        fill: {
          type: "gradient",
        },
        chart: {
          type: "donut",
        },
        legend: {
          position: "bottom",
        },
        tooltip: {
          enabled: true,
          y: {
            show: true,
            formatter: (val) => {
              val = this.$options.filters.currency_symbol(
                  this.$options.filters.toMoney(val)
              );
              return val;
            },
          },
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 200,
              },
              legend: {
                position: "bottom",
              },
            },
          },
        ],
      },
      analyticsData: null,
    };
  },
  computed: {
    topSales() {
      let data = this.analyticsData;
      if (data) {
        let results = data.filter((item) => {
          return item.sales_amount > 0;
        });
        return results;
      }
    },
    current_year() {
      return this.$store.state.current_year;
    },
    labels() {
      return this.chartLabels;
    },
    expense_graph() {
      const expens = this.expenseAccounts;

      const limit = 5;
      let totalExpenses = 0;
      expens.forEach((item) => {
        if (item.balance) {
          totalExpenses += item.balance;
        }
      });

      const sortedExpenses = expens
          .filter((e) => Number(e.balance) != 0)
          .sort((a, b) => Number(b.balance) - Number(a.balance));
      const topExpenses = [];
      let otherExpenseTotal = 0;
      sortedExpenses.forEach((expense, i) => {
        if (expense.account_name) {
          if (i < limit) {
            topExpenses.push(
                this.formatExpense(expense, Number(totalExpenses))
            );
          } else {
            otherExpenseTotal += Number(expense.balance);
          }
        }
      });
      const otherExpense = [
        this.formatOtherExpense(totalExpenses, otherExpenseTotal),
        otherExpenseTotal.toFixed(2),
      ];
      return otherExpenseTotal > 0
          ? [...topExpenses, otherExpense]
          : [...topExpenses];
    },
    business() {
      return this.$store.state.user.user_infor.current_business;
    },
    TOTAL_SALE() {
      let sum = 0;
      if (this.analyticsData) {
        this.analyticsData.forEach((item) => {
          sum += Number(item.sales_amount);
        });
        return sum;
      }
      return sum;
    },
  },
  watch: {
    current_year() {
      this.get_expense();
    },
  },
  methods: {
    calculatePercentage(item) {
      let perc = (item.sales_amount / this.TOTAL_SALE) * 100;
      return Math.ceil(Number(perc));
    },
    formatExpense(expense, sum) {
      const {account_name, balance} = expense;
      const expensePercent = ((100 / sum) * balance).toFixed(2);
      const label = `${account_name} (${expensePercent}%)`;
      return [label, balance.toFixed(2)];
    },

    formatOtherExpense(sum, otherSum) {
      const percent = ((100 / sum) * otherSum).toFixed(2);
      return `Others (${percent}%)`;
    },

    cache_accounts() {
      localStorage.setItem(
          "accounts." + this.$store.state.user.user_infor.current_business.id,
          JSON.stringify(this.$store.state.user.countryInfo)
      );
    },
    async get_expense(month = null) {
      let start_date =
          month === null
              ? moment().format(this.$store.state.current_year + "-01-01")
              : moment([this.$store.state.current_year, month.number])
                  .startOf("month")
                  .format("YYYY-MM-DD");
      let end_date =
          month === null
              ? moment().format(this.current_year + "-12-31")
              : moment([this.$store.state.current_year, month.number])
                  .endOf("month")
                  .format("YYYY-MM-DD");
      this.progress = true;
      let expenseByRange = await axios.get(
          `/api/v2/dashboard-expenses-by-range?from=${start_date}&to=${end_date}`
      );
      this.expenseAccounts = expenseByRange.data;
      this.series = this.expense_graph.map((list) => Number(list[1]));
      this.chartOptions.labels = this.expense_graph.map(
          (list) => list[0].split(" ")[0]
      );
      this.progress = false;
    },
    getTopSales(month = null) {
      let start_date =
          month === null
              ? moment().format(this.$store.state.current_year + "-01-01")
              : moment([this.$store.state.current_year, month.number])
                  .startOf("month")
                  .format("YYYY-MM-DD");
      let end_date =
          month === null
              ? moment().format(this.current_year + "-12-31")
              : moment([this.$store.state.current_year, month.number])
                  .endOf("month")
                  .format("YYYY-MM-DD");
      this.progress = true;
      axios
          .get(`/api/analytics/top-sales/${start_date}/${end_date}`)
          // .get(`/api/analytics/products/${start_date}/${end_date}`)
          .then((res) => {
            this.analyticsData = res.data;
            // this.analyticsData = this.transformProducts(this.analyticsData);
            this.progress = false;
          })
          .catch((err) => {
            if (err.response.status === 403) {
              console.log("Test");
            }
          });
    },
    transformProducts(products) {
      return products.map((product) => {
        return {
          name: product.name,
          purchases: product.purchase_amount,
          quantity_purchased: product.purchase_quantity,
          sales_amount: product.invoice_amount + product.sales_amount,
          quantity_sold:
              Number(product.sales_quantity) + Number(product.invoice_quantity),
          margin: (
              product.invoice_amount +
              product.sales_amount -
              product.purchase_amount
          ).toFixed(2),
        };
      });
    },
  },
  created() {
    eventBus.$on("monthly", (month) => {
      this.getTopSales(month);
      this.get_expense(month);
    });
    eventBus.$on("yearly", (year) => {
      this.get_expense(null);
      this.getTopSales(null);
    });
  },
  mounted() {
    // this.get_expense();
  },
};
</script>

<style>
rect {
  fill: none !important;
}
</style>
